import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, User, BookOpen, ClipboardList, School as SchoolIcon, Loader2 } from "lucide-react";

const ICON = { user: User, homework: BookOpen, exam: ClipboardList, class: SchoolIcon };

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); ref.current?.querySelector("input")?.focus(); setOpen(true); }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClickOutside); document.removeEventListener("keydown", onKey); };
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`); setResults(data.results || []); }
      catch { setResults([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(timerRef.current);
  }, [q]);

  const go = (r) => { nav(r.link); setOpen(false); setQ(""); };

  return (
    <div ref={ref} className="relative flex-1 max-w-md hidden md:block">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
        <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder="Ara: öğrenci, ödev, sınav... (Ctrl+K)"
          className="pl-9 pr-4 h-9 bg-foreground/[0.03] border-transparent focus-visible:bg-background"
          data-testid="global-search-input"/>
      </div>
      {open && q.trim().length >= 2 && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-popover border border-border rounded-xl shadow-lg overflow-hidden z-50 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center"><Loader2 size={16} className="animate-spin mx-auto"/></div>
          ) : results.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">Sonuç yok</div>
          ) : results.map((r) => {
            const Icon = ICON[r.type] || Search;
            return (
              <button key={`${r.type}-${r.id}`} onClick={() => go(r)} data-testid={`search-result-${r.type}-${r.id}`}
                className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-foreground/[0.04] transition-colors">
                <div className="w-8 h-8 rounded-lg grid place-items-center bg-foreground/[0.05] shrink-0"><Icon size={14}/></div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{r.title}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{r.subtitle}</div>
                </div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-widest shrink-0">{r.type}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
